import { useCallback, useEffect, useRef } from "react";
import type { GridPaginationModel, GridSortModel } from "@mui/x-data-grid-premium";
import {
  applyAssessmentCenterPaginationChange,
  clampServerPaginationPage,
} from "./assessmentCenterPagination";

/**
 * Keep the last known server total while react-query loads a new page key.
 * Passing rowCount=0 to MUI DataGrid resets server pagination to page 0
 * (same class of bug as homepage RegulationsTable — preserve totalRows on error, 399c77c).
 */
export function useStableServerRowCount(totalCount: number | undefined): number {
  const lastTotalRef = useRef(0);

  if (
    totalCount !== undefined &&
    Number.isFinite(totalCount) &&
    totalCount >= 0
  ) {
    lastTotalRef.current = totalCount;
  }

  return totalCount ?? lastTotalRef.current;
}

/** Clamp page when `totalCount` changes and current page is out of range (§7). */
export function useClampAssessmentCenterPage(
  totalCount: number | undefined,
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>,
) {
  useEffect(() => {
    if (totalCount === undefined) return;
    setPaginationModel((prev) => {
      const clamped = clampServerPaginationPage(
        prev.page,
        totalCount,
        prev.pageSize,
      );
      if (clamped === prev.page) return prev;
      return { ...prev, page: clamped };
    });
  }, [totalCount, setPaginationModel]);
}

/** Pagination + sort handlers that keep page N on sort (§4) and reset on page size (§9). */
export function useAssessmentCenterPaginationHandlers(
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>,
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>,
) {
  const sortChangedRef = useRef(false);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      sortChangedRef.current = true;
      setSortModel(model);
    },
    [setSortModel],
  );

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      const sortGuard = sortChangedRef.current;
      if (sortGuard) {
        sortChangedRef.current = false;
      }
      setPaginationModel((prev) =>
        applyAssessmentCenterPaginationChange(prev, newModel, sortGuard),
      );
    },
    [setPaginationModel],
  );

  return { handleSortModelChange, handlePaginationModelChange };
}
